import { AlertCircle, CheckCircle, X, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProfile } from '../../../Contexts/ProfileContext';
import Activities from '../../../components/Activities/Activity';
import Header from '../../../components/Header';
import Loader from '../../../components/Loader/Loader';
import RejectButton from '../../../components/RejectButton/RejectButton';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import apiService from '../../../services/apiService';
import {
  downloadOperationInstructionPdf,
  getOperationInstructionByID,
  deleteOperationInstructionByID,
  markAuthorized,
  markChecked,
  markReceived,
  notifyAuthorize,
  notifyCheck,
} from '../../../services/operationInstructionServices';
import InfoApparatusList from './InfoApparatusList';
import AddOperatingInstruction from '../../superAdmin/OperatingInstruction/addOperatingInstructionModal';

interface StatusDetails {
  id: number;
  code: string;
  title: string;
  color: string;
  created_at: string;
  updated_at: string;
}

interface OperationInstruction {
  id: number;
  reference_number: string;
  title: string;
  site: string;
  instruction_date: string;
  description: string | null;
  created_by: string;
  status: string;
  'status-details': StatusDetails;
  allowed_actions: string[];
  workflow_stages?: any[];
  apparatus: {
    id: number;
    operating_instruction_id: number;
    name: string;
    instruction: string | null;
    created_at: string;
    updated_at: string;
  }[];
}

export default function InfoInstruction() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const profile = useProfile();

  const [instruction, setInstruction] = useState<OperationInstruction | null>(null);
  const [loading, setLoading] = useState(true);

  const [currentUserId, setCurrentUserId] = useState<string>('unknown_user');

  const [confirmCheckIds, setConfirmCheckIds] = useState<string[]>([]);
  const [confirmAuthorizeIds, setConfirmAuthorizeIds] = useState<string[]>([]);
  const [confirmReceivedIds, setConfirmReceivedIds] = useState<string[]>([]);

  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [showMarkCheckedModal, setShowMarkCheckedModal] = useState(false);
  const [showNotifyAuthorizeModal, setShowNotifyAuthorizeModal] = useState(false);
  const [showAuthorizeModal, setShowAuthorizeModal] = useState(false);
  const [showReceivedModal, setShowReceivedModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [selectedTSO, setSelectedTSO] = useState<{ id: number; name: string } | null>(null);
  const [selectedOM, setSelectedOM] = useState<{ id: number; name: string } | null>(null);

  const [notifyLoading, setNotifyLoading] = useState(false);
  const [markCheckedLoading, setMarkCheckedLoading] = useState(false);
  const [notifyAuthorizeLoading, setNotifyAuthorizeLoading] = useState(false);
  const [authorizeLoading, setAuthorizeLoading] = useState(false);
  const [receivedLoading, setReceivedLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [apiError, setApiError] = useState('');

  const [tsoList, setTsoList] = useState<{ id: number; name: string }[]>([]);
  const [omList, setOmList] = useState<{ id: number; name: string }[]>([]);

  const [showDownloadButton, setShowDownloadButton] = useState(false);

  useEffect(() => {
    const userId = localStorage.getItem('currentUserID') || localStorage.getItem('userId') || 'unknown_user';
    setCurrentUserId(userId);
  }, []);

  const fetchInstruction = async () => {
    try {
      const data = await getOperationInstructionByID(id!);

      const getPendingApproverIds = (workflowStages: any[], stageName: string) =>
        (Array.isArray(workflowStages) ? workflowStages : [])
          .filter((stage: any) => stage?.stage === stageName)
          .flatMap((stage: any) =>
            (Array.isArray(stage?.approvers) ? stage.approvers : [])
              .filter((a: any) => a?.status === 'PENDING')
              .map((a: any) => (a?.user?.id ?? a?.user_id ?? a?.id)?.toString())
              .filter(Boolean),
          );

      setConfirmCheckIds(getPendingApproverIds(data.workflow_stages || [], 'CHECK'));
      setConfirmAuthorizeIds(getPendingApproverIds(data.workflow_stages || [], 'AUTHORIZE'));
      setConfirmReceivedIds(getPendingApproverIds(data.workflow_stages || [], 'RECEIVE'));

      setInstruction(data);
    } catch (error: any) {
      console.error(error);
      const message = error?.response?.data?.message || error?.message || 'Failed to load operation instruction details';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  const fetchTSOList = async () => {
    try {
      const response = await apiService.get('/users?filter[user_type_id]=11');
      setTsoList(response.data.data || response.data);
    } catch (error) {
      console.error('Failed to fetch TSO list:', error);
    }
  };

  const fetchOMList = async () => {
    try {
      const response = await apiService.get('/users?filter[user_type_id]=9');
      setOmList(response.data.data || response.data);
    } catch (error) {
      console.error('Failed to fetch OM list:', error);
    }
  };

  useEffect(() => {
    if (id) fetchInstruction();
    fetchTSOList();
    fetchOMList();
  }, [id]);

  const handleNotifyToUpdate = async () => {
    if (!selectedTSO) return;
    try {
      setNotifyLoading(true);
      await notifyCheck(id!, { user_ids: [selectedTSO.id] });
      await fetchInstruction();
      setShowNotifyModal(false);
      setSelectedTSO(null);
      setSuccessMessage('Notification successfully sent to the Technical Support Officer to check.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to send notification.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleMarkChecked = async () => {
    try {
      setMarkCheckedLoading(true);
      await markChecked(id!);
      await fetchInstruction();
      setShowMarkCheckedModal(false);
      setSuccessMessage('Marked as checked successfully.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to mark as checked.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setMarkCheckedLoading(false);
    }
  };

  const handleNotifyAuthorize = async () => {
    if (!selectedOM) return;
    try {
      setNotifyAuthorizeLoading(true);
      await notifyAuthorize(id!, { user_ids: [selectedOM.id] });
      await fetchInstruction();
      setShowNotifyAuthorizeModal(false);
      setSelectedOM(null);
      setSuccessMessage('Notification successfully sent to the Operation Manager to authorize.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to send notification.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setNotifyAuthorizeLoading(false);
    }
  };

  const handleAuthorize = async () => {
    try {
      setAuthorizeLoading(true);
      await markAuthorized(id!);
      await fetchInstruction();
      setShowAuthorizeModal(false);
      setSuccessMessage('Operating instruction authorized successfully.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to authorize.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setAuthorizeLoading(false);
    }
  };

  const handleReceived = async () => {
    try {
      setReceivedLoading(true);
      await markReceived(id!);
      await fetchInstruction();
      setShowReceivedModal(false);
      setShowDownloadButton(true);
      setSuccessMessage('Operating instruction marked as received successfully.');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to mark as received.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setReceivedLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadLoading(true);
      const pdfBlob = await downloadOperationInstructionPdf(id!);

      const url = window.URL.createObjectURL(new Blob([pdfBlob], { type: 'application/pdf' }));

      const link = document.createElement('a');
      link.href = url;

      link.setAttribute('download', `${instruction.reference_number}.pdf`);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to download PDF';

      setApiError(message);
      setShowError(true);

      setTimeout(() => setShowError(false), 5000);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!instruction) return;
    try {
      setDeleteLoading(true);
      await deleteOperationInstructionByID(instruction.id);
      setShowDeleteModal(false);
      navigate('/OperatingInstruction');
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete instruction.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) return <Loader />;
  if (!instruction) return <div className="p-6 text-red-500">No instruction found</div>;

  const statusCode = instruction['status-details']?.code ?? '';
  const isNewStatus = instruction.status === 'New';
  const isCreator = instruction.created_by?.toString() === currentUserId;

  return (
    <>
      <Header title="Operating Instruction Details" showBackButton />

      <div className="grid grid-cols-12">
        {/* LEFT CONTENT */}
        <div className="col-span-8 min-w-0 px-6 py-6">
          {/* HEADER ROW: Ref number/status on left — icons + action button parallel in one flex row on right */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                {instruction.reference_number}
                <span className="text-orange-500 text-sm font-medium">● {instruction.status}</span>
              </h2>

              <p className="text-xs text-gray-400 mt-1">
                {instruction.instruction_date ? new Date(instruction.instruction_date).toLocaleDateString('en-GB') : '-'}
              </p>
            </div>

            {/* Single parallel row: icons + button all on the same flex line */}
            <div className="flex items-center gap-3">
              {isNewStatus && isCreator && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="p-2 rounded-md border border-gray-300 text-red-500 hover:bg-red-50"
                    aria-label="Delete instruction"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="p-2 rounded-md border border-gray-300 text-blue-500 hover:bg-blue-50"
                    aria-label="Edit instruction"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              )}

              {!confirmCheckIds.length &&
                profile?.role === 'shift_supervisor' &&
                instruction.created_by?.toString() === currentUserId &&
                statusCode !== 'CHECK_PENDING' &&
                statusCode !== 'CHECKED' &&
                instruction.status === 'New' && (
                  <button
                    onClick={() => setShowNotifyModal(true)}
                    className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Notify to Check
                  </button>
                )}

              {statusCode === 'CHECK_PENDING' &&
                profile?.role === 'technical_support_officer' &&
                confirmCheckIds.includes(currentUserId) && (
                  <div className="flex items-center gap-3">
                    <RejectButton title="" id={instruction.id} apiPath="operating-instructions" onSuccess={fetchInstruction} />
                    <button
                      onClick={() => setShowMarkCheckedModal(true)}
                      className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                    >
                      Mark as Checked
                    </button>
                  </div>
                )}

              {confirmAuthorizeIds &&
                profile?.role === 'shift_supervisor' &&
                instruction.created_by?.toString() === currentUserId &&
                statusCode !== 'AUTH_PENDING' &&
                statusCode !== 'AUTH' &&
                instruction.status === 'Checked' && (
                  <button
                    onClick={() => setShowNotifyAuthorizeModal(true)}
                    className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Notify to Authorize
                  </button>
                )}

              {statusCode === 'AUTH_PENDING' && profile?.role === 'operation_manager' && confirmAuthorizeIds.includes(currentUserId) && (
                <div className="flex items-center gap-3">
                  <RejectButton title="" id={instruction.id} apiPath="operating-instructions" onSuccess={fetchInstruction} />
                  <button
                    onClick={() => setShowAuthorizeModal(true)}
                    className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Authorize
                  </button>
                </div>
              )}

              {statusCode === 'AUTHORIZED' &&
                profile?.role === 'shift_supervisor' &&
                instruction.created_by?.toString() === currentUserId && (
                  <button
                    onClick={() => setShowReceivedModal(true)}
                    className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Mark as Received
                  </button>
                )}

              {instruction.status === 'Received' &&
                profile?.role === 'shift_supervisor' &&
                instruction.created_by?.toString() === currentUserId && (
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloadLoading}
                    className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed"
                  >
                    {downloadLoading ? 'Downloading...' : 'Download'}
                  </button>
                )}
            </div>
          </div>

          <div className="grid min-w-0 grid-cols-2 gap-8 mb-6">
            <div className="min-w-0">
              <h2 className="text-gray-400 text-sm">Title</h2>
              <p className="font-medium text-gray-900 whitespace-normal break-words [overflow-wrap:anywhere]">{instruction.title}</p>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-400 text-sm">Site</h2>
              <p className="font-medium text-gray-900 whitespace-normal break-words [overflow-wrap:anywhere]">{instruction.site}</p>
            </div>
          </div>

          <InfoApparatusList apparatus={instruction.apparatus} />
        </div>

        {/* VERTICAL DIVIDER */}
        <div className="col-span-1 flex justify-end pr-6">
          <div className="w-px bg-gray-200 h-full translate-x-6" />
        </div>

        {/* ACTIVITIES */}
        <div className="col-span-3 py-6 pr-6">
          <Activities moduleName="operating_instructions" moduleItemId={Number(id)} />
        </div>
      </div>

      <AddOperatingInstruction
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onConfirm={async () => {
          setShowEditModal(false);
          setSuccessMessage('Operating instruction updated successfully.');
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 5000);
          await fetchInstruction();
        }}
        onError={(errorMsg: string) => {
          setApiError(errorMsg);
          setShowError(true);
          setTimeout(() => setShowError(false), 5000);
        }}
        editData={instruction}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Are you sure you want to delete this instruction?"
        message=""
        confirmText="Yes, Delete"
        loadingText="Deleting.."
        isLoading={deleteLoading}
        cancelText="Cancel"
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />

      {showNotifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 relative">
            <button
              onClick={() => {
                setShowNotifyModal(false);
                setSelectedTSO(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-base font-semibold text-gray-800 mb-4">Notify to Check</h2>
            <label className="block text-sm text-gray-600 mb-1">Select Technical Support Officer</label>
            <select
              value={selectedTSO?.id ?? ''}
              onChange={(e) => {
                const found = tsoList.find((t) => t.id === Number(e.target.value));
                setSelectedTSO(found ?? null);
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select Technical Support Officer</option>
              {tsoList.map((tso) => (
                <option key={tso.id} value={tso.id}>
                  {tso.name}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNotifyModal(false);
                  setSelectedTSO(null);
                }}
                className="px-4 py-2 border rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleNotifyToUpdate}
                disabled={!selectedTSO || notifyLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:bg-red-300"
              >
                {notifyLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMarkCheckedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 relative">
            <button onClick={() => setShowMarkCheckedModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
            <p className="text-center text-gray-800 text-sm font-medium mb-6">Are you sure you want to mark OIs as checked?</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setShowMarkCheckedModal(false)} className="px-6 py-2 border rounded-md">
                Cancel
              </button>
              <button
                onClick={handleMarkChecked}
                disabled={markCheckedLoading}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:bg-red-300"
              >
                {markCheckedLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotifyAuthorizeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6 relative">
            <button
              onClick={() => {
                setShowNotifyAuthorizeModal(false);
                setSelectedOM(null);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-base font-semibold text-gray-800 mb-4">Notify to Authorize</h2>
            <label className="block text-sm text-gray-600 mb-1">Select Operations Manager</label>
            <select
              value={selectedOM?.id ?? ''}
              onChange={(e) => {
                const found = omList.find((o) => o.id === Number(e.target.value));
                setSelectedOM(found ?? null);
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="">Select Operations Manager</option>
              {omList.map((om) => (
                <option key={om.id} value={om.id}>
                  {om.name}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowNotifyAuthorizeModal(false);
                  setSelectedOM(null);
                }}
                className="px-4 py-2 border rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={handleNotifyAuthorize}
                disabled={!selectedOM || notifyAuthorizeLoading}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:bg-red-300"
              >
                {notifyAuthorizeLoading ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAuthorizeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 relative">
            <button onClick={() => setShowAuthorizeModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
            <p className="text-center text-gray-800 text-sm font-medium mb-6">Are you sure you want to authorize?</p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setShowAuthorizeModal(false)} className="px-6 py-2 border rounded-md">
                Cancel
              </button>
              <button
                onClick={handleAuthorize}
                disabled={authorizeLoading}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:bg-red-300"
              >
                {authorizeLoading ? 'Authorizing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReceivedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 p-6 relative">
            <button onClick={() => setShowReceivedModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
            <p className="text-center text-gray-800 text-sm font-medium mb-6">
              Are you sure you want to mark this instruction as received?
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setShowReceivedModal(false)} className="px-6 py-2 border rounded-md">
                Cancel
              </button>
              <button
                onClick={handleReceived}
                disabled={receivedLoading}
                className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 disabled:bg-red-300"
              >
                {receivedLoading ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="fixed bottom-6 right-6 bg-green-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px]">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-6 h-6 mt-0.5" />
            <div>
              <h4 className="font-semibold">Success</h4>
              <p className="text-sm">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {showError && (
        <div className="fixed bottom-6 right-6 bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px]">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 mt-0.5" />
            <div>
              <h4 className="font-semibold">Error</h4>
              <p className="text-sm">{apiError}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
